import Serializable              from '@/core/misc/mixins/Serializable';
import Utils                     from '@/core/utils/Utils';
import Characteristic            from '@/domains/Characteristics/Characteristic';
import EntityPointer             from '@/domains/EntityPointer/EntityPointer';
import NumericRange              from '@/core/types/NumericRange';
import CharacteristicAllowedValue from "@/domains/Characteristics/CharacteristicAllowedValue";
import NoSerialize from "@/core/misc/decorators/NoSerialize";
import serializeSimplified from '@/core/misc/decorators/SerializeSimplified';

/**
 * link и string типы записывают значение в stringValue
 * numeric в numberValue
 * selectableSingle и selectableMulti в listValue
 * entityLink и image в entityPointerValues
 * */

/**
 * Общий предок для значений характеристики у товара и оффера.
 *
 * Разница между наследниками:
 *   - ProductCharacteristic.numericValue — NumericRange
 *   - OfferCharacteristic.numericValue   — number
 *   - ProductCharacteristic имеет дополнительное поле productId
 *
 * Формат от бека:
 *   {
 *     characteristic: { id, name, valueType, unit, allowedValues, ... },
 *     valueType: 'numeric' | 'string' | 'selectableSingle' | ...,
 *     stringValue, numericValue, listValue, entityPointerValues
 *   }
 */
export default abstract class AbstractCharacteristicValue extends Serializable {
  /** Рамка. Бэку при сохранении нужен только id — остальное он берёт из своей копии рамки. */
  @serializeSimplified
  characteristic: Characteristic;

  valueType: string;

  stringValue: string;
  listValue: Array<CharacteristicAllowedValue> | null = null;
  entityPointerValues: EntityPointer[] = [];

  /**
   * Заполняет общие поля объекта из ответа сервера.
   * Наследник создаёт экземпляр, зовёт super.fromServer, затем добавляет
   * свои специфичные поля (numericValue с разным типом, productId).
   */
  static fromServer(data: any, value?: AbstractCharacteristicValue): AbstractCharacteristicValue {
    if (value === undefined) {
      throw new Error('AbstractCharacteristicValue.fromServer вызывается только из наследника с готовым инстансом');
    }

    super.fromServer(data, value);

    value.characteristic      = Utils.hydrate(data.characteristic, Characteristic.fromServer);
    value.entityPointerValues = Utils.hydrateArray(data.entityPointerValues, EntityPointer.fromServer);
    value.listValue = Utils.hydrateArray(data.listValue, CharacteristicAllowedValue.fromServer);

    return value;
  }

  static fromCharacteristic(data: Characteristic, value: AbstractCharacteristicValue): AbstractCharacteristicValue {
    super.fromServer(data, value);

    value.characteristic      = Utils.hydrate(data, Characteristic.fromServer);

    return value;
  }

  abstract getNumericValue(): NumericRange | number | null;
  abstract setNumericValue(value: unknown): void;

  @NoSerialize
  get valueStringRepresentation() {
    switch (this.valueType) {
      case Characteristic.VALUE_TYPE_STRING:
      case Characteristic.VALUE_TYPE_LINK: {
        return this.stringValue;
      }
      case Characteristic.VALUE_TYPE_NUMERIC: {
        return this.getNumericValue();
      }
      case Characteristic.VALUE_TYPE_SELECTABLE_SINGLE:
      case Characteristic.VALUE_TYPE_SELECTABLE_MULTI: {
        return (this.listValue ?? []).map((_item) => _item.name).join(', ');
      }
      case Characteristic.VALUE_TYPE_ENTITY_LINK:
      case Characteristic.VALUE_TYPE_IMAGE: {
        // Для OfferChar и для ProductChar один родитель.
        // Поставщик в OfferChar ничего не прикрепляет, то есть значения быть не может
        // Но покупатель может что-то прикрепить, а вот тут надо отображать уже что-то
        if (Utils.isEmptyArray(this.entityPointerValues)) return '--';
        return this.entityPointerValues;
      }
      default: {
        return null;
      }
    }
  }
  get value(): unknown {
    switch (this.valueType) {
      case Characteristic.VALUE_TYPE_STRING:
      case Characteristic.VALUE_TYPE_LINK: {
        return this.stringValue;
      }
      case Characteristic.VALUE_TYPE_NUMERIC: {
        return this.getNumericValue();
      }
      case Characteristic.VALUE_TYPE_SELECTABLE_SINGLE:
      case Characteristic.VALUE_TYPE_SELECTABLE_MULTI: {
        return this.listValue;
      }
      case Characteristic.VALUE_TYPE_ENTITY_LINK:
      case Characteristic.VALUE_TYPE_IMAGE: {
        return this.entityPointerValues;
      }
      default: {
        return null;
      }
    }
  }

  set value(val: unknown) {
    switch (this.valueType) {
      case Characteristic.VALUE_TYPE_STRING:
      case Characteristic.VALUE_TYPE_LINK:
        this.stringValue = val as string;
        break;
      case Characteristic.VALUE_TYPE_NUMERIC:
        this.setNumericValue(val);
        break;
      case Characteristic.VALUE_TYPE_SELECTABLE_SINGLE:
      case Characteristic.VALUE_TYPE_SELECTABLE_MULTI:
        this.listValue = val as Array<{ id: number; name: string }>;
        break;
      case Characteristic.VALUE_TYPE_ENTITY_LINK:
      case Characteristic.VALUE_TYPE_IMAGE:
        this.entityPointerValues = val ? val : [];
        break;
    }
  }

  @NoSerialize
  get isImage() { return this.characteristic.isImage; }

  @NoSerialize
  get isEntityLink() { return this.characteristic.isEntityLink; }

  @NoSerialize
  get isString() { return this.characteristic.isString; }

  @NoSerialize
  get isLink() { return this.characteristic.isLink; }

  @NoSerialize
  get isNumeric() { return this.characteristic.isNumeric; }

  @NoSerialize
  get isSelectableSingle() { return this.characteristic.isSelectableSingle }

  @NoSerialize
  get isSelectableMulti() {  return this.characteristic.isSelectableMulti }


  // проксирую свойство для быстрого набора
  @NoSerialize
  get validations() { return this.characteristic?.validations; }

  @NoSerialize
  get name() {return this.characteristic.name }

  @NoSerialize
  get allowedValues() {return this.characteristic.allowedValues}

  @NoSerialize
  get isReadOnly() { return this.characteristic.isReadOnly }
}